import styled, { css } from 'styled-components'
import { GridProps, GridItemProps } from './Grid.types'

const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
}

const getSpacingValue = (
  value: string | number | undefined
): string | undefined => {
  if (value === undefined) return undefined
  if (typeof value === 'number') return `${value}px`
  return value
}

const getGridColumns = (span?: number): string | undefined => {
  if (!span) return undefined
  return `span ${span} / span ${span}`
}

const containerStyles = css<GridProps>`
  display: grid;

  ${({ columns }) =>
    columns &&
    `grid-template-columns: ${
      typeof columns === 'number' ? `repeat(${columns}, 1fr)` : columns
    };`}

  ${({ rows }) =>
    rows &&
    `grid-template-rows: ${
      typeof rows === 'number' ? `repeat(${rows}, 1fr)` : rows
    };`}
  
  ${({ templateColumns }) =>
    templateColumns && `grid-template-columns: ${templateColumns};`}
  ${({ templateRows }) =>
    templateRows && `grid-template-rows: ${templateRows};`}
  ${({ templateAreas }) =>
    templateAreas && `grid-template-areas: ${templateAreas};`}
  
  ${({ gap, spacing }) => {
    const gapValue = gap ?? spacing
    return gapValue !== undefined && `gap: ${getSpacingValue(gapValue)};`
  }}
  ${({ rowGap }) =>
    rowGap !== undefined && `row-gap: ${getSpacingValue(rowGap)};`}
  ${({ columnGap }) =>
    columnGap !== undefined && `column-gap: ${getSpacingValue(columnGap)};`}
  
  ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
  ${({ justifyItems }) => justifyItems && `justify-items: ${justifyItems};`}
  ${({ alignContent }) => alignContent && `align-content: ${alignContent};`}
  ${({ justifyContent }) =>
    justifyContent && `justify-content: ${justifyContent};`}
  
  ${({ autoFlow }) => autoFlow && `grid-auto-flow: ${autoFlow};`}
  ${({ autoColumns }) => autoColumns && `grid-auto-columns: ${autoColumns};`}
  ${({ autoRows }) => autoRows && `grid-auto-rows: ${autoRows};`}
`

const flexContainerStyles = css<GridProps>`
  display: flex;
  flex-wrap: ${({ wrap = 'wrap' }) => wrap};
  flex-direction: ${({ direction = 'row' }) => direction};

  ${({ gap, spacing }) => {
    const gapValue = gap ?? (spacing ? spacing * 8 : undefined)
    return gapValue !== undefined && `gap: ${getSpacingValue(gapValue)};`
  }}

  ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
  ${({ justifyContent }) =>
    justifyContent && `justify-content: ${justifyContent};`}
`

export const StyledGrid = styled.div<GridProps>`
  ${({ container = true, columns }) =>
    container && columns ? containerStyles : flexContainerStyles}
`

const getResponsiveWidth = (size?: number): string => {
  if (!size) return 'auto'
  return `${(size / 12) * 100}%`
}

export const StyledGridItem = styled.div<GridItemProps>`
  ${({ gridColumn, colSpan }) => {
    const column = gridColumn ?? (colSpan ? getGridColumns(colSpan) : undefined)
    return column && `grid-column: ${column};`
  }}

  ${({ gridRow, rowSpan }) => {
    const row =
      gridRow ?? (rowSpan ? `span ${rowSpan} / span ${rowSpan}` : undefined)
    return row && `grid-row: ${row};`
  }}
  
  ${({ gridArea }) => gridArea && `grid-area: ${gridArea};`}
  
  ${({ colStart }) => colStart && `grid-column-start: ${colStart};`}
  ${({ colEnd }) => colEnd && `grid-column-end: ${colEnd};`}
  ${({ rowStart }) => rowStart && `grid-row-start: ${rowStart};`}
  ${({ rowEnd }) => rowEnd && `grid-row-end: ${rowEnd};`}
  
  ${({ alignSelf }) => alignSelf && `align-self: ${alignSelf};`}
  ${({ justifySelf }) => justifySelf && `justify-self: ${justifySelf};`}
  
  /* Responsive widths for flex-based grid */
  ${({ xs }) =>
    xs &&
    `
    flex: 0 0 ${getResponsiveWidth(xs)};
    max-width: ${getResponsiveWidth(xs)};
  `}
  
  ${({ sm }) =>
    sm &&
    `
    @media (min-width: ${BREAKPOINTS.sm}px) {
      flex: 0 0 ${getResponsiveWidth(sm)};
      max-width: ${getResponsiveWidth(sm)};
    }
  `}
  
  ${({ md }) =>
    md &&
    `
    @media (min-width: ${BREAKPOINTS.md}px) {
      flex: 0 0 ${getResponsiveWidth(md)};
      max-width: ${getResponsiveWidth(md)};
    }
  `}
  
  ${({ lg }) =>
    lg &&
    `
    @media (min-width: ${BREAKPOINTS.lg}px) {
      flex: 0 0 ${getResponsiveWidth(lg)};
      max-width: ${getResponsiveWidth(lg)};
    }
  `}
  
  ${({ xl }) =>
    xl &&
    `
    @media (min-width: ${BREAKPOINTS.xl}px) {
      flex: 0 0 ${getResponsiveWidth(xl)};
      max-width: ${getResponsiveWidth(xl)};
    }
  `}
`
